(() => {
  if (window.ProhealthAutomaticGifts) return;

  const ROOT_SELECTOR = '[data-ph-automatic-gifts]';
  const GIFT_PROPERTY = '_free_gift';
  const OFFER_PROPERTY = '_ph_auto_offer';
  const PROMO_PROPERTY = '_promo';
  const LABEL_PROPERTY = '_ph_gift_label';
  const COUPON_STORAGE_KEY = 'minimog-discount-code';
  const COUPON_SUPPRESSION_KEY = 'ph-auto-gift-suppressed-by-coupon';
  const SYNC_DEBOUNCE_MS = 120;
  const GIFT_PRICE_GRACE_MS = 6000;
  const GIFT_PRICE_RECHECK_MS = 1500;

  let running = false;
  // A cart change made by the customer or the theme while a sync is running
  // used to be ignored, so a second NUUN added quickly never earned the gift.
  // It is now remembered and a fresh sync runs as soon as the current one ends.
  let resyncPending = false;
  let scheduledTimer = null;
  let lastCouponState = false;
  let productCache = Object.create(null);

  const rootUrl = () => window.Shopify?.routes?.root || '/';
  const normalise = (value) => String(value || '').toLowerCase().trim();

  const moduleEl = () => document.querySelector(ROOT_SELECTOR);

  const getSettings = () => {
    const module = moduleEl();
    if (!module) return null;
    const configNode = module.querySelector('[data-ph-automatic-gifts-config]');
    let offers = [];
    try {
      offers = JSON.parse(configNode?.textContent || '[]');
    } catch (error) {
      console.error('Automatic gift configuration error:', error);
    }
    return {
      module,
      enabled: module.dataset.enabled !== 'false',
      disableWithCoupon: module.dataset.disableWithCoupon !== 'false',
      removeIfNotFree: module.dataset.removeIfNotFree !== 'false',
      addedMessage: module.dataset.addedMessage || '',
      removedCouponMessage: module.dataset.removedCouponMessage || '',
      priceErrorMessage: module.dataset.priceErrorMessage || '',
      conflictTitle: module.dataset.conflictTitle || 'Choose your offer',
      conflictMessage: module.dataset.conflictMessage || 'This coupon cannot be combined with the promotional free gift currently in your cart.',
      continueCouponLabel: module.dataset.continueCouponLabel || 'Use coupon and remove gift',
      keepGiftLabel: module.dataset.keepGiftLabel || 'Keep my free gift',
      offers: offers.filter((offer) => offer && offer.enabled !== false)
    };
  };

  const getCart = async () => {
    const response = await fetch(`${rootUrl()}cart.js`, {
      headers: { Accept: 'application/json' },
      cache: 'no-store'
    });
    if (!response.ok) throw new Error('Unable to retrieve the latest cart.');
    return response.json();
  };

  const getProduct = async (handle) => {
    if (!handle) return null;
    if (productCache[handle]) return productCache[handle];
    const response = await fetch(`${rootUrl()}products/${encodeURIComponent(handle)}.js`, {
      headers: { Accept: 'application/json' },
      cache: 'force-cache'
    });
    if (!response.ok) throw new Error(`Unable to read product ${handle}.`);
    const product = await response.json();
    productCache[handle] = product;
    return product;
  };

  const isAutomaticGift = (item) =>
    item?.properties?.[GIFT_PROPERTY] === 'true' && Boolean(item?.properties?.[OFFER_PROPERTY]);

  // An offer can carry several DIFFERENT gifts, each with its own quantity.
  // Slots come from the section settings; the single product / variant ID
  // settings remain as the fallback for offers configured before slots existed.
  const offerGiftItems = (offer) => {
    const configured = Array.isArray(offer?.giftItems) ? offer.giftItems : [];
    const items = [];
    for (const entry of configured) {
      const id = Number(entry?.id || 0);
      if (id <= 0) continue;
      const quantity = Math.max(Number(entry?.quantity || 1), 1);
      const existing = items.find((item) => item.variantId === id);
      // The same product picked in two slots means more units of it, not two lines.
      if (existing) existing.quantity += quantity;
      else items.push({ variantId: id, quantity });
    }
    if (!items.length) {
      const legacy = Number(offer?.giftVariantId || 0);
      if (legacy > 0) items.push({ variantId: legacy, quantity: Math.max(Number(offer?.giftQuantity || 1), 1) });
    }
    return items;
  };

  const offerGiftVariants = (offer) => offerGiftItems(offer).map((item) => item.variantId);

  const giftLinesForOffer = (cart, offerId, variantId = null) =>
    (cart?.items || []).filter(
      (item) =>
        isAutomaticGift(item) &&
        String(item.properties[OFFER_PROPERTY]) === String(offerId) &&
        (variantId === null || Number(item.variant_id) === Number(variantId))
    );

  const applicableCouponCodes = (cart) => {
    const codes = Array.isArray(cart?.discount_codes)
      ? cart.discount_codes
          .filter((discount) => discount && discount.applicable !== false && discount.code)
          .map((discount) => String(discount.code).trim())
          .filter(Boolean)
      : [];

    if (codes.length) return codes;

    const stored = String(localStorage.getItem(COUPON_STORAGE_KEY) || '')
      .split(',')
      .map((code) => code.trim())
      .filter(Boolean);
    return stored;
  };

  const hasCoupon = (cart, explicitState) => {
    if (typeof explicitState === 'boolean') return explicitState;
    return applicableCouponCodes(cart).length > 0;
  };

  // --- Collection membership --------------------------------------------
  //
  // cart.js does not say which collections a product is in, so the section
  // answers it in Liquid for the products in the cart (see the membership
  // JSON in the section). Membership does not change during a page view, so
  // each product is looked up once; the section is re-rendered only when a
  // product nobody has checked yet reaches the cart.
  const checkedProducts = new Set();
  const collectionMembers = new Map();

  const readMembership = (node) => {
    if (!node) return;
    let data = null;
    try {
      data = JSON.parse(node.textContent || '{}');
    } catch (error) {
      console.warn('Automatic gift membership error:', error);
      return;
    }
    for (const id of data?.checked || []) checkedProducts.add(Number(id));
    for (const [offerId, ids] of Object.entries(data?.offers || {})) {
      if (!collectionMembers.has(offerId)) collectionMembers.set(offerId, new Set());
      const members = collectionMembers.get(offerId);
      for (const id of ids || []) members.add(Number(id));
    }
  };

  const readInitialMembership = () =>
    readMembership(moduleEl()?.querySelector('[data-ph-automatic-gifts-membership]'));

  const refreshMembership = async (cart, offers) => {
    if (!offers.some((offer) => offer.qualifierType === 'collections')) return;
    const unknown = (cart?.items || []).some(
      (item) => !isAutomaticGift(item) && !checkedProducts.has(Number(item.product_id))
    );
    if (!unknown) return;
    try {
      const response = await fetch(`${rootUrl()}?sections=prohealth-automatic-gifts`, {
        headers: { Accept: 'application/json' },
        cache: 'no-store'
      });
      if (!response.ok) throw new Error('Unable to read collection membership.');
      const sections = await response.json();
      const html = sections?.['prohealth-automatic-gifts'] || '';
      const doc = new DOMParser().parseFromString(html, 'text/html');
      readMembership(doc.querySelector('[data-ph-automatic-gifts-membership]'));
    } catch (error) {
      // Fails closed: an unconfirmed product does not count towards the offer.
      console.warn('Automatic gift collection lookup failed:', error);
    }
  };

  const lineQualifies = async (item, offer) => {
    if (offer.qualifierType === 'product_tag') {
      const requiredTag = normalise(offer.qualifierTag);
      if (!requiredTag || !item.handle) return false;
      try {
        const product = await getProduct(item.handle);
        return (product?.tags || []).some((tag) => normalise(tag) === requiredTag);
      } catch (error) {
        console.warn('Automatic gift tag lookup failed:', error);
        return false;
      }
    }
    if (offer.qualifierType === 'collections') {
      return Boolean(collectionMembers.get(String(offer.id))?.has(Number(item.product_id)));
    }
    const requiredHandles = String(offer.qualifierHandles || '')
      .split('|')
      .map((handle) => normalise(handle))
      .filter(Boolean);
    return requiredHandles.includes(normalise(item.handle));
  };

  // Paid lines only: promotional gifts and the cart-wide ladder gift never
  // count towards an offer, whether it is judged on quantity or on spend.
  const qualifyingLines = async (cart, offer) => {
    const lines = [];
    for (const item of cart?.items || []) {
      if (isAutomaticGift(item) || item?.properties?._ph_unlock_gift === 'true') continue;
      if (await lineQualifies(item, offer)) lines.push(item);
    }
    return lines;
  };

  const qualifyingQuantity = async (cart, offer) =>
    (await qualifyingLines(cart, offer)).reduce((sum, item) => sum + Number(item.quantity || 0), 0);

  const discountTitles = (item) =>
    (item?.line_level_discount_allocations || [])
      .map((allocation) => allocation?.discount_application?.title || allocation?.discount_application?.key || '')
      .filter(Boolean);

  // Shopify discounts already working for OTHER offers: the titles found on
  // their gift lines, minus any title also found on this offer's own gifts.
  const otherOfferDiscountTitles = (cart, offer) => {
    const own = new Set();
    const other = new Set();
    for (const item of cart?.items || []) {
      if (!isAutomaticGift(item)) continue;
      const target = String(item.properties[OFFER_PROPERTY]) === String(offer.id) ? own : other;
      for (const title of discountTitles(item)) target.add(title);
    }
    for (const title of own) other.delete(title);
    return other;
  };

  // Two ways of counting a minimum spend, chosen per offer in the Theme Editor.
  //
  // At full price (original_line_price, in cents), as Shopify counts a Buy X
  // Get Y minimum purchase amount. Tested live, 3 Oct 2026: Shopify does NOT
  // count a unit that is already the "Customer buys" item of another Buy X
  // Get Y, and splits such a unit onto its own line carrying the other
  // discount, so those lines are left out. Only for gifts given by a Shopify
  // Buy X Get Y; otherwise the gift is added, charged, then withdrawn.
  const fullPriceSpend = async (cart, offer) => {
    const used = otherOfferDiscountTitles(cart, offer);
    return (await qualifyingLines(cart, offer)).reduce((sum, item) => {
      if (discountTitles(item).some((title) => used.has(title))) return sum;
      const line = item.original_line_price != null
        ? Number(item.original_line_price)
        : Number(item.original_price || item.price || 0) * Number(item.quantity || 0);
      return sum + line;
    }, 0);
  };

  // After discounts: what the customer pays for the qualifying products.
  // final_line_price already carries line-level discounts (automatic product
  // discounts, product codes). Order-level discounts (an order code such as
  // "10% off") sit on the cart, not on the lines, so their amount is shared
  // across the lines by value, the way Shopify allocates it at checkout.
  // Used for gifts priced at €0, which no Shopify discount checks: the theme
  // is the only place the minimum is enforced. Orders #9825-#9883 (Oct 2026)
  // received the €0 Anthelios at €48-€55 because spend was counted at full price.
  const afterDiscountSpend = async (cart, offer) => {
    const lines = await qualifyingLines(cart, offer);
    const qualifying = lines.reduce(
      (sum, item) => sum + Number(item.final_line_price != null ? item.final_line_price : item.line_price || 0),
      0
    );
    const subtotal = Number(cart?.items_subtotal_price || 0);
    const orderDiscount = (Array.isArray(cart?.cart_level_discount_applications)
      ? cart.cart_level_discount_applications
      : []
    ).reduce((sum, discount) => sum + Number(discount?.total_allocated_amount || 0), 0);
    if (orderDiscount <= 0 || subtotal <= 0 || qualifying <= 0) return Math.max(qualifying, 0);
    const share = Math.min(qualifying / subtotal, 1);
    return Math.max(Math.round(qualifying - orderDiscount * share), 0);
  };

  const qualifyingSpend = async (cart, offer) =>
    offer.spendBasis === 'full_price' ? fullPriceSpend(cart, offer) : afterDiscountSpend(cart, offer);

  // How many times the offer has been earned. Each gift slot is then multiplied
  // by its own quantity, so "buy 2, get gift A and gift B" is two lines of one,
  // not two units of the same product.
  const offerQualifications = async (cart, offer, couponSuppressed) => {
    const requiredQuantity = Math.max(Number(offer.requiredQuantity || 1), 1);
    if (!offerGiftItems(offer).length || couponSuppressed) return 0;

    // Minimum-spend offers are earned once per order, as Shopify applies them.
    // An offer without an amount is treated as switched off rather than free.
    if (offer.triggerType === 'spend') {
      const minimum = Number(offer.minimumSpend || 0);
      if (minimum <= 0) return 0;
      const spend = await qualifyingSpend(cart, offer);
      // A spend offer withdrawn by price protection is offered again once the
      // customer spends more, instead of staying off for the whole page view.
      const blockedAt = spendAtPriceBlock.get(String(offer.id));
      if (blockedAt != null && spend > blockedAt) {
        spendAtPriceBlock.delete(String(offer.id));
        for (const gift of offerGiftItems(offer)) priceBlockedGifts.delete(graceKey(offer.id, gift.variantId));
      }
      return spend >= minimum ? 1 : 0;
    }

    const qualifierQty = await qualifyingQuantity(cart, offer);
    if (qualifierQty < requiredQuantity) return 0;

    const qualifications = offer.repeatOffer === false ? 1 : Math.floor(qualifierQty / requiredQuantity);
    return Math.max(qualifications, 0);
  };

  const desiredQuantityForGift = (offer, gift, qualifications) => {
    let desired = qualifications * Math.max(Number(gift.quantity || 1), 1);
    const maxGifts = Number(offer.maxGifts || 0);
    if (maxGifts > 0) desired = Math.min(desired, maxGifts);
    return desired;
  };

  // When Shopify grants fewer free units than the offer would give (for
  // example "maximum number of uses per order" is set on the discount while
  // "Repeat gift" is on here), the offer is trimmed to what Shopify grants
  // instead of being withdrawn. The cap is spread one unit at a time across
  // the gift slots, so "get Mango + Orange" capped at 2 stays one of each.
  const grantedCaps = new Map();

  // A gift that Shopify refused to add (draft, sold out, deleted) or that
  // price protection withdrew is held at 0. Holding it here, not only where
  // lines are written, keeps the price check from expecting a free unit that
  // can never arrive and withdrawing the offer's other gifts because of it.
  const desiredGiftQuantities = (offer, qualifications) => {
    const gifts = offerGiftItems(offer).map((gift) => ({
      variantId: gift.variantId,
      quantity: isGiftHeld(offer.id, gift.variantId) ? 0 : desiredQuantityForGift(offer, gift, qualifications)
    }));
    const cap = grantedCaps.get(String(offer.id));
    const total = gifts.reduce((sum, gift) => sum + gift.quantity, 0);
    if (cap == null || total <= cap) return gifts;

    const capped = gifts.map((gift) => ({ variantId: gift.variantId, quantity: 0 }));
    let left = cap;
    while (left > 0) {
      let progressed = false;
      for (let index = 0; index < gifts.length && left > 0; index += 1) {
        if (capped[index].quantity < gifts[index].quantity) {
          capped[index].quantity += 1;
          left -= 1;
          progressed = true;
        }
      }
      if (!progressed) break;
    }
    return capped;
  };

  const OWN_REQUEST_HEADER = 'X-PH-Auto-Gift';

  const changeLine = async (key, quantity) => {
    const response = await fetch(`${rootUrl()}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', [OWN_REQUEST_HEADER]: '1' },
      body: JSON.stringify({ id: key, quantity })
    });
    if (!response.ok) throw new Error('Unable to update promotional gift.');
    return response.json();
  };

  const addGift = async (offer, variantId, quantity) => {
    const response = await fetch(`${rootUrl()}cart/add.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json', [OWN_REQUEST_HEADER]: '1' },
      body: JSON.stringify({
        id: Number(variantId),
        quantity,
        properties: {
          [GIFT_PROPERTY]: 'true',
          [OFFER_PROPERTY]: String(offer.id),
          [PROMO_PROPERTY]: String(offer.name || 'Automatic gift'),
          // Customer-facing tag for a gift priced at zero, which has no Shopify
          // discount to name it in the cart. Omitted when the setting is empty.
          ...(offer.lineLabel ? { [LABEL_PROPERTY]: String(offer.lineLabel) } : {})
        }
      })
    });
    if (!response.ok) {
      const error = new Error('Unable to add promotional gift.');
      error.status = response.status;
      throw error;
    }
    return response.json();
  };

  // Brings the cart in line with the offer: every configured gift product at
  // desiredQty, duplicate lines consolidated, and lines for gift products that
  // are no longer part of the offer removed.
  const setOfferGifts = async (cart, offer, qualifications) => {
    const gifts = desiredGiftQuantities(offer, qualifications);
    const variants = gifts.map((gift) => gift.variantId);
    let latest = cart;
    let changed = false;
    let added = false;

    let safety = 0;
    while (safety < 40) {
      const stale = giftLinesForOffer(latest, offer.id).find(
        (item) => !variants.includes(Number(item.variant_id))
      );
      if (!stale) break;
      latest = await changeLine(stale.key, 0);
      changed = true;
      safety += 1;
    }

    for (const gift of gifts) {
      const variantId = gift.variantId;
      const desiredQty = gift.quantity;
      let lines = giftLinesForOffer(latest, offer.id, variantId);
      const currentQty = lines.reduce((sum, item) => sum + Number(item.quantity || 0), 0);

      if (desiredQty <= 0) {
        let guard = 0;
        while (lines.length && guard < 20) {
          latest = await changeLine(lines[0].key, 0);
          changed = true;
          guard += 1;
          lines = giftLinesForOffer(latest, offer.id, variantId);
        }
        continue;
      }

      if (!lines.length) {
        // cart/add.js returns the added line, not the cart, so re-read it.
        try {
          await addGift(offer, variantId, desiredQty);
        } catch (error) {
          // 404 / 422: Shopify will not sell this variant (draft, sold out,
          // deleted). Skip this gift for the rest of the page view and keep
          // going, so the offer's other gifts and every other offer still run.
          if (error.status === 404 || error.status === 422) {
            unavailableGifts.add(graceKey(offer.id, variantId));
            console.warn('Automatic gift skipped, Shopify could not add it:', {
              offer: offer.name, variantId
            });
            continue;
          }
          throw error;
        }
        startGiftGrace(offer.id);
        latest = await getCart();
        changed = true;
        added = true;
        continue;
      }

      if (currentQty === desiredQty && lines.length === 1) continue;

      latest = await changeLine(lines[0].key, desiredQty);
      changed = true;
      if (desiredQty > currentQty) added = true;
      lines = giftLinesForOffer(latest, offer.id, variantId);
      let guard = 0;
      while (lines.length > 1 && guard < 20) {
        latest = await changeLine(lines[1].key, 0);
        guard += 1;
        lines = giftLinesForOffer(latest, offer.id, variantId);
      }
    }

    return { cart: latest, changed, added };
  };

  // --- Gift pricing check -------------------------------------------------
  //
  // Shopify decides which cart lines a Buy X Get Y discount falls on: when the
  // "Customer gets" products are also sellable, it discounts the lowest-priced
  // eligible units, which may be units the customer bought rather than the
  // lines we added. Asking "is MY gift line at zero?" therefore produced the
  // false "not discounted to free" warning on a promotion that was working.
  //
  // The question asked here is the commercial one: has the cart received as
  // many free units of this offer's gift products as the offer promises? If it
  // has, the promotion is being honoured and the gifts stay, wherever Shopify
  // chose to show the zero.
  const freeUnitsInLine = (item) => {
    const unitPrice = Number(item.original_price || item.price || 0);
    if (unitPrice <= 0) return Number(item.quantity || 0);
    const discount = Number(
      item.line_level_total_discount != null ? item.line_level_total_discount : item.total_discount || 0
    );
    if (discount <= 0) return 0;
    return Math.min(Math.floor(discount / unitPrice + 0.001), Number(item.quantity || 0));
  };

  const offerFreeUnits = (cart, offer) => {
    const variants = offerGiftVariants(offer);
    let free = 0;
    for (const item of cart?.items || []) {
      if (!variants.includes(Number(item.variant_id))) continue;
      free += freeUnitsInLine(item);
    }
    return free;
  };

  const offerExpectedUnits = (offer, qualifications) =>
    desiredGiftQuantities(offer, qualifications).reduce((sum, gift) => sum + gift.quantity, 0);

  const offerDiscountSatisfied = (cart, offer, qualifications) => {
    if (!offerGiftItems(offer).length || qualifications <= 0) return true;
    const expected = offerExpectedUnits(offer, qualifications);
    if (expected <= 0) return true;
    return offerFreeUnits(cart, offer) >= expected;
  };

  const waitForAutomaticDiscountSettlement = async (cart, offerStates, attempts = 4, delay = 250) => {
    let latest = cart;
    for (let attempt = 0; attempt < attempts; attempt += 1) {
      const pending = offerStates.filter(
        (state) =>
          giftLinesForOffer(latest, state.offer.id).length &&
          !offerDiscountSatisfied(latest, state.offer, state.qualifications)
      );
      if (!pending.length) return latest;
      await new Promise((resolve) => window.setTimeout(resolve, delay));
      latest = await getCart();
    }
    return latest;
  };

  // Shopify needs a moment to apply the discount to a gift that was just added,
  // and with more than one gift they land one at a time, so an offer is only
  // judged once it has failed the check for the whole grace window. The grace
  // starts at the add, and a re-check is scheduled so the window is reached.
  const giftGrace = new Map();
  // An offer withdrawn by price protection is not re-offered for the rest of the
  // page view. Without this the engine re-adds the gifts on the next sync, they
  // are charged again, and the customer watches the gifts and the warning cycle.
  const priceBlockedGifts = new Set();
  // Gifts Shopify refused to add to the cart during this page view.
  const unavailableGifts = new Set();
  // Spend offers withdrawn by price protection, with the spend at that moment.
  const spendAtPriceBlock = new Map();
  const graceKey = (offerId, variantId) => `${offerId}:${variantId}`;
  const isGiftHeld = (offerId, variantId) => {
    const key = graceKey(offerId, variantId);
    return priceBlockedGifts.has(key) || unavailableGifts.has(key);
  };

  const startGiftGrace = (offerId) => {
    if (!giftGrace.has(offerId)) giftGrace.set(offerId, Date.now());
  };

  const enforceGiftPricing = async (cart, offerStates) => {
    let latest = cart;
    let removed = false;
    let trimmedAny = false;
    let waiting = false;
    const now = Date.now();

    for (const { offer, qualifications } of offerStates) {
      try {
        if (!giftLinesForOffer(latest, offer.id).length) {
          giftGrace.delete(offer.id);
          continue;
        }
        if (offerDiscountSatisfied(latest, offer, qualifications)) {
          giftGrace.delete(offer.id);
          continue;
        }
        if (!giftGrace.has(offer.id)) giftGrace.set(offer.id, now);
        if (now - giftGrace.get(offer.id) < GIFT_PRICE_GRACE_MS) {
          waiting = true;
          continue;
        }

        giftGrace.delete(offer.id);
        const expected = offerExpectedUnits(offer, qualifications);
        const free = offerFreeUnits(latest, offer);

        // Shopify grants some free units, just fewer than this offer promised.
        // Keep the gifts Shopify pays for and take off only the charged extra.
        if (free > 0 && free < expected) {
          console.warn('Automatic gift offer trimmed to what Shopify discounts:', {
            offer: offer.name, expected, free
          });
          grantedCaps.set(String(offer.id), free);
          const trimmed = await setOfferGifts(latest, offer, qualifications);
          latest = trimmed.cart || latest;
          trimmedAny = trimmedAny || trimmed.changed;
          continue;
        }

        // The discount is not being applied at all. Withdraw this offer's gifts
        // so the customer is never charged for something presented as free.
        console.warn('Automatic gift offer withdrawn, Shopify did not discount it:', {
          offer: offer.name, expected, free
        });
        let guard = 0;
        let remaining = giftLinesForOffer(latest, offer.id);
        while (remaining.length && guard < 20) {
          latest = await changeLine(remaining[0].key, 0);
          removed = true;
          guard += 1;
          remaining = giftLinesForOffer(latest, offer.id);
        }
        for (const gift of offerGiftItems(offer)) {
          priceBlockedGifts.add(graceKey(offer.id, gift.variantId));
        }
        if (offer.triggerType === 'spend') {
          spendAtPriceBlock.set(String(offer.id), await qualifyingSpend(latest, offer));
        }
      } catch (error) {
        // One offer failing its price check must not stop the others being judged.
        console.error(`Automatic gift price check error in "${offer.name}":`, error);
      }
    }

    if (waiting) scheduleSync(GIFT_PRICE_RECHECK_MS);
    return { cart: latest, removed, trimmed: trimmedAny };
  };

  const refreshThemeCart = () => {
    const drawer = document.querySelector('m-cart-drawer');
    const page = document.querySelector('m-cart');
    if (drawer && typeof drawer.onCartDrawerUpdate === 'function') drawer.onCartDrawerUpdate(true);
    if (page && typeof page.onCartUpdate === 'function') page.onCartUpdate(true);
    if (window.MinimogEvents && window.MinimogTheme?.pubSubEvents?.cartUpdate) {
      window.MinimogEvents.emit(window.MinimogTheme.pubSubEvents.cartUpdate, {
        source: 'prohealth-automatic-gifts'
      });
    }
  };

  // Rendered here, INSIDE the notice host, with a lifetime this file owns.
  // The theme's Notification inserts itself as a sibling of its target, so
  // the notice landed loose in the drawer header, which no cart re-render
  // replaces, and stayed there after the problem was gone. Same fix as the
  // cart notices in prohealth-cart-unlocked-gifts.js; same markup.
  const NOTICE_MS = 6000;
  let noticeTimer = null;

  const notify = (settings, message, type = 'warning') => {
    if (!message) return;
    const host =
      document.querySelector('.m-cart-drawer--active [data-ph-cart-notice-host]') ||
      document.querySelector('m-cart [data-ph-cart-notice-host]') ||
      document.querySelector('[data-ph-cart-notice-host]');
    if (!host) return;

    const SMILE = 'M14.828 14.828a4 4 0 01-5.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
    const FROWN = 'M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z';
    const notice = document.createElement('div');
    notice.className = `notification ${type} show`;
    notice.setAttribute('role', 'status');
    notice.innerHTML =
      '<svg class="m-svg-icon--large" fill="none" stroke="currentColor" viewBox="0 0 24 24" ' +
      'xmlns="http://www.w3.org/2000/svg" aria-hidden="true" focusable="false">' +
      '<path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="' +
      (type === 'success' ? SMILE : FROWN) +
      '"/></svg><div class="notification__text"></div>';
    notice.querySelector('.notification__text').textContent = message;

    window.clearTimeout(noticeTimer);
    host.replaceChildren(notice);
    noticeTimer = window.setTimeout(() => notice.remove(), NOTICE_MS);
  };

  const suppressedCouponCode = () => String(localStorage.getItem(COUPON_SUPPRESSION_KEY) || '').trim();

  const isSuppressedByCoupon = (cart) => {
    const storedCode = normalise(suppressedCouponCode());
    if (!storedCode) return false;
    const currentCodes = applicableCouponCodes(cart).map(normalise);
    const active = currentCodes.includes(storedCode);
    if (!active) localStorage.removeItem(COUPON_SUPPRESSION_KEY);
    return active;
  };

  const promotionalGiftLines = (cart) => (cart?.items || []).filter(isAutomaticGift);

  const removePromotionalGifts = async (cart = null) => {
    if (running) return { cart: cart || null, removed: false };
    running = true;
    try {
      let latest = cart || await getCart();
      let removed = false;
      let safety = 0;

      // Shopify can regenerate line-item keys whenever discounts are recalculated.
      // Always remove one promotional line from the latest cart response, then
      // re-read the remaining gift line from that fresh response before continuing.
      while (safety < 20) {
        const line = promotionalGiftLines(latest)[0];
        if (!line) break;
        latest = await changeLine(line.key, 0);
        removed = true;
        safety += 1;
      }

      return { cart: latest, removed };
    } finally {
      running = false;
      if (resyncPending) {
        resyncPending = false;
        scheduleSync();
      }
    }
  };

  const setCouponSuppression = (code) => {
    const value = String(code || '').trim();
    if (value) localStorage.setItem(COUPON_SUPPRESSION_KEY, value);
    else localStorage.removeItem(COUPON_SUPPRESSION_KEY);
  };

  const clearCouponSuppression = () => localStorage.removeItem(COUPON_SUPPRESSION_KEY);

  const sync = async ({ cart: suppliedCart = null, forceCouponSuppression = false } = {}) => {
    if (running) return suppliedCart;
    const settings = getSettings();
    if (!settings?.enabled) return suppliedCart;

    running = true;
    try {
      let cart = suppliedCart || await getCart();
      const couponSuppressed = settings.disableWithCoupon && (forceCouponSuppression || isSuppressedByCoupon(cart));
      const giftCountBefore = (cart.items || []).filter(isAutomaticGift).length;
      let changed = false;
      let giftAdded = false;

      const offerStates = [];
      await refreshMembership(cart, settings.offers);

      // Each offer is settled on its own. Offers stack (a customer can earn the
      // NUUN, PURE, Vital Proteins and spend gifts in one order), so an error
      // in one offer must never stop the offers after it from running.
      // Product offers first, spend offers last: a spend offer can only be
      // judged once the other offers' gifts are in and Shopify has shown which
      // units it uses for them (see qualifyingSpend).
      const orderedOffers = [
        ...settings.offers.filter((offer) => offer.triggerType !== 'spend'),
        ...settings.offers.filter((offer) => offer.triggerType === 'spend')
      ];
      for (const offer of orderedOffers) {
        try {
          const qualifications = await offerQualifications(cart, offer, couponSuppressed);
          if (qualifications <= 0) grantedCaps.delete(String(offer.id));
          offerStates.push({ offer, qualifications });
          const result = await setOfferGifts(cart, offer, qualifications);
          if (result.changed) {
            changed = true;
            giftAdded = giftAdded || result.added;
            cart = await getCart();
          } else if (result.cart) {
            cart = result.cart;
          }
        } catch (error) {
          console.error(`Automatic gift error in "${offer.name}":`, error);
          // Lines may have changed before the error: continue from the real cart.
          changed = true;
          cart = await getCart();
        }
      }

      // Repaint as soon as the gift lines are on the cart. Waiting for the
      // discount settlement below is what made the gift appear seconds late.
      if (changed) refreshThemeCart();

      let priceProtectionRemoved = false;
      let priceProtectionTrimmed = false;
      if (!couponSuppressed && settings.removeIfNotFree) {
        // Shopify needs a moment to recalculate its automatic discounts, and with
        // several gifts they land one at a time. Wait for the settled cart before
        // judging whether an offer's discount is being applied at all.
        cart = await waitForAutomaticDiscountSettlement(cart, offerStates);
        const enforced = await enforceGiftPricing(cart, offerStates);
        priceProtectionRemoved = enforced.removed;
        priceProtectionTrimmed = Boolean(enforced.trimmed);
        if (priceProtectionRemoved || enforced.trimmed) {
          changed = true;
          cart = await getCart();
        } else if (enforced.cart) {
          cart = enforced.cart;
        }
      }

      if (priceProtectionRemoved || priceProtectionTrimmed) refreshThemeCart();

      const giftCountAfter = (cart.items || []).filter(isAutomaticGift).length;
      if (couponSuppressed && giftCountBefore > 0 && giftCountAfter === 0 && !lastCouponState) {
        notify(settings, settings.removedCouponMessage, 'warning');
      } else if (priceProtectionRemoved) {
        notify(settings, settings.priceErrorMessage, 'warning');
      } else if (giftAdded && giftCountAfter > giftCountBefore) {
        notify(settings, settings.addedMessage, 'success');
      }

      lastCouponState = couponSuppressed;
      return cart;
    } catch (error) {
      console.error('Automatic gift error:', error);
      return suppliedCart;
    } finally {
      running = false;
      if (resyncPending) {
        resyncPending = false;
        scheduleSync();
      }
    }
  };

  const scheduleSync = (delay = SYNC_DEBOUNCE_MS) => {
    window.clearTimeout(scheduledTimer);
    scheduledTimer = window.setTimeout(() => sync(), delay);
  };

  const originalFetch = window.fetch.bind(window);
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    try {
      const requestUrl = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
      if (/\/cart\/(add|change|update)(\.js)?(?:\?|$)/.test(requestUrl)) {
        const headers = args[1]?.headers || (typeof args[0] === 'object' ? args[0]?.headers : null);
        const own = headers && (typeof headers.get === 'function'
          ? headers.get(OWN_REQUEST_HEADER) === '1'
          : headers[OWN_REQUEST_HEADER] === '1');
        if (!own) {
          if (running) resyncPending = true;
          else scheduleSync();
        }
      }
    } catch (error) {}
    return response;
  };

  // Wrapped: these listeners receive an event, which must not be read as a delay.
  document.addEventListener('DOMContentLoaded', () => {
    readInitialMembership();
    scheduleSync();
  }, { once: true });
  document.addEventListener('shopify:section:load', () => {
    // Collections may have been changed in the Theme Editor: start again.
    checkedProducts.clear();
    collectionMembers.clear();
    readInitialMembership();
    scheduleSync();
  });

  window.ProhealthAutomaticGifts = {
    sync,
    scheduleSync,
    getCart,
    getSettings,
    hasPromotionalGifts: (cart) => promotionalGiftLines(cart).length > 0,
    removePromotionalGifts,
    setCouponSuppression,
    clearCouponSuppression,
    afterCartMutation: (cart) => sync({ cart }),
    afterDiscountMutation: (cart, options = {}) => {
      if (options.suppressCode) setCouponSuppression(options.suppressCode);
      if (options.clearSuppression) clearCouponSuppression();
      return sync({ cart, forceCouponSuppression: Boolean(options.suppressCode) });
    }
  };
})();
